import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Books, MagnifyingGlass, Copy, CaretDown, CaretUp } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  ["", "All"], ["subgroup_analysis", "Subgroup Analysis"], ["comparator_challenge", "Comparator"],
  ["local_applicability", "Local Applicability"], ["indirect_treatment_comparison", "ITC"],
  ["budget_impact", "Budget Impact"], ["cost_effectiveness", "Cost-Effectiveness"], ["safety", "Safety"],
  ["dossier_content", "Dossier Content"], ["pricing_reimbursement", "Pricing"], ["other", "Other"],
];

function LibraryCard({ q }) {
  const [open, setOpen] = useState(false);
  const cur = (q.versions || [])[q.versions?.length - 1] || {};
  return (
    <div data-testid={`library-card-${q.id}`} className="bg-white border border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-3.5 text-left hover:bg-slate-50/60">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-emerald-700 text-white">Approved</span>
          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-slate-300 text-slate-500">
            {(q.analysis?.classification || "other").replace(/_/g, " ")}
          </span>
          <span className="font-mono-data text-[10px] text-slate-400 ml-auto">
            {q.country_name} · {new Date(q.approved_at || q.created_at).toLocaleDateString()}
          </span>
          {open ? <CaretUp size={12} className="text-slate-400" /> : <CaretDown size={12} className="text-slate-400" />}
        </div>
        <p className="text-xs font-semibold text-slate-800 mt-1.5 leading-relaxed">{q.question}</p>
        {!open && <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{cur.affiliate_response}</p>}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/40 space-y-3">
          <div>
            <p className="overline-label text-slate-400">Approved affiliate response</p>
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap mt-1">{cur.affiliate_response}</p>
          </div>
          {(q.analysis?.evidence_used || []).length > 0 && (
            <p className="text-[11px] text-slate-500"><span className="font-semibold">Evidence used:</span> {q.analysis.evidence_used.join(" · ")}</p>
          )}
          <button data-testid={`library-copy-${q.id}`}
            onClick={() => navigator.clipboard.writeText(cur.affiliate_response || "").then(() => toast.success("Response copied — reuse it in a new query or local dossier")).catch(() => toast.error("Clipboard unavailable"))}
            className="border border-slate-300 text-[11px] font-semibold px-4 py-2 hover:bg-white flex items-center gap-1.5">
            <Copy size={12} /> Copy for reuse
          </button>
        </div>
      )}
    </div>
  );
}

export default function LibraryPage() {
  const { productId, product } = useProduct();
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("");
  const [country, setCountry] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback((s) => {
    const params = {};
    if (category) params.category = category;
    if (country) params.country_id = country;
    if (s ?? search) params.search = s ?? search;
    return axios.get(`${API}/queries/library/${productId}`, { params }).then((r) => setItems(r.data));
  }, [productId, category, country, search]);

  useEffect(() => { setItems(null); if (productId) load().catch(console.error); }, [productId, category, country]); // eslint-disable-line

  const countries = [...new Map((items || []).map((q) => [q.country_id, q.country_name])).entries()];
  return (
    <PageShell pageId="library" overline={`MAI Knowledge Hub™ · ${product?.name || ""}`} title="Response Library">
      <div className="flex flex-wrap items-center gap-2">
        {CATEGORIES.map(([k, label]) => (
          <button key={k} data-testid={`library-cat-${k || "all"}`} onClick={() => setCategory(k)}
            className={`text-[11px] font-semibold px-3 py-1.5 border ${category === k ? "bg-slate-900 text-white border-slate-900" : "text-slate-500 border-slate-200 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="library-country-filter" className="text-[11px] border border-slate-200 px-2 py-1.5 bg-white text-slate-600"
            value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">All markets</option>
            {countries.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <div className="flex items-center border border-slate-200 bg-white">
            <input data-testid="library-search-input" className="text-[11px] px-2.5 py-1.5 w-44 focus:outline-none" placeholder="Search responses…"
              value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
            <button data-testid="library-search-btn" onClick={() => load()} className="px-2 text-slate-400 hover:text-slate-900"><MagnifyingGlass size={13} /></button>
          </div>
        </div>
      </div>
      {!items ? (
        <div className="py-24 text-center text-slate-400 text-sm">Loading library…</div>
      ) : items.length === 0 ? (
        <div data-testid="library-empty" className="bg-white border border-slate-200 py-16 text-center">
          <Books size={32} className="text-slate-300 mx-auto" />
          <p className="font-heading font-bold text-sm text-slate-700 mt-3">No approved responses match.</p>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">Every response approved in Incoming Queries lands here — searchable, filterable and reusable by any market.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[10px] text-slate-400">{items.length} approved response{items.length > 1 ? "s" : ""} — your organisation's institutional memory</p>
          {items.map((q) => <LibraryCard key={q.id} q={q} />)}
        </div>
      )}
    </PageShell>
  );
}
